import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { EditorPlanItem } from './types';

interface TreatmentItemRowProps {
  item: EditorPlanItem;
  isDisabled: boolean;
  isReadOnly: boolean;
  onChange: (item: EditorPlanItem) => void;
  onRemove: () => void;
}

export function TreatmentItemRow({
  item,
  isDisabled,
  isReadOnly,
  onChange,
  onRemove,
}: TreatmentItemRowProps) {
  const { t } = useTranslation('plans');

  return (
    <div className="border border-zooborder rounded-2xl p-4 bg-zoooffwhite">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-bold text-zooindigo min-w-0">{item.treatmentName}</p>
        {isReadOnly ? null : (
          <Button
            variant="destructiveGhost"
            size="icon"
            onClick={onRemove}
            disabled={isDisabled}
            aria-label={t('editor.removeTreatment')}
            className="shrink-0"
          >
            <Icon name="Trash" />
          </Button>
        )}
      </div>

      {isReadOnly ? (
        item.instructions.trim().length === 0 ? null : (
          <p className="text-xs text-zoomuted mt-2 whitespace-pre-wrap">{item.instructions}</p>
        )
      ) : (
        <label className="block text-xs font-semibold text-zoomuted mt-3">
          {t('editor.instructions')}
          <textarea
            value={item.instructions}
            disabled={isDisabled}
            rows={2}
            placeholder={t('editor.instructionsPlaceholder')}
            onChange={function change(event) {
              onChange({ ...item, instructions: event.target.value });
            }}
            className="mt-1 w-full rounded-xl border border-zooborder bg-zoowhite px-3 py-2 text-sm text-zooindigo"
          />
        </label>
      )}

      {item.progress === null ? null : (
        <div className="flex flex-wrap gap-2 mt-3">
          <Badge tone="success" size="sm" className="text-[11px]">
            {t('editor.progressDone', { count: item.progress.done })}
          </Badge>
          {item.progress.skipped === 0 ? null : (
            <Badge tone="muted" size="sm" className="text-[11px]">
              {t('editor.progressSkipped', { count: item.progress.skipped })}
            </Badge>
          )}
          {item.progress.refused === 0 ? null : (
            <Badge tone="danger" size="sm" className="text-[11px]">
              {t('editor.progressRefused', { count: item.progress.refused })}
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}
